<template>
  <section class="admin-content" id="online-payments">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 12px;">
            <div class="header-icon-wrap"><i class="fe fe-send" style="font-size: 22px;"></i></div>
            <div class="flex-grow-1">
              <h4 class="mb-0 font-weight-bold">Online Payments</h4>
              <small style="opacity: 0.8;">Capitation and claims paid from the agency's Safehaven account to facilities' accounts</small>
            </div>
            <span v-if="settings" class="badge px-3 py-2" :class="settings.enabled ? 'badge-success' : 'badge-light'">
              {{ settings.enabled ? "Online payments ON" : "Online payments OFF on the server" }}
            </span>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <ul class="nav nav-tabs page-tabs mb-3">
            <li class="nav-item" v-for="t in tabs" :key="t.key"><a href="#" class="nav-link" :class="{ active: tab === t.key }" @click.prevent="setTab(t.key)"><i class="fe mr-1" :class="t.icon"></i> {{ t.label }}</a></li>
          </ul>

          <!-- ── Capitation ─────────────────────────────────── -->
          <div v-if="tab === 'capitation'">
            <div class="card shadow-sm table-card mb-3">
              <div class="card-body d-flex flex-wrap align-items-end" style="gap: 12px;">
                <div><label class="small font-weight-bold text-secondary d-block">Month</label><input type="month" class="form-control form-control-sm" v-model="period" :max="thisMonth" @change="preview" /></div>
                <div v-if="cap"><span class="stat-label">Total due</span><div class="stat-value">₦{{ fmt(cap.total) }}</div></div>
                <div v-if="cap"><span class="stat-label">Facilities</span><div class="stat-value">{{ cap.facilities.length }}</div></div>
                <div v-if="cap"><span class="stat-label">Ready to pay</span><div class="stat-value text-success">₦{{ fmt(readyTotal) }}</div><small class="text-muted">{{ readyCount }} with a payout account</small></div>
                <div class="ml-auto text-right" v-if="settings && settings.can_prepare">
                  <button class="btn btn-approve" :disabled="!cap || !readyCount || busy" @click="prepareCapitation">
                    <span v-if="busy === 'cap'" class="spinner-border spinner-border-sm mr-1"></span><i v-else class="fe fe-play mr-1"></i> Prepare all capitation for {{ periodLabel }}
                  </button>
                  <div class="small text-muted mt-1">Goes to an approver before any money is sent.</div>
                </div>
              </div>
            </div>
            <div v-if="cap && cap.missing_rates.length" class="alert alert-warning small">
              <i class="fe fe-alert-triangle mr-1"></i> No capitation rate for: {{ cap.missing_rates.map((m) => `${m.sector} (${fmt(m.enrollees)} enrollees)`).join(", ") }}. They are not paid until a rate is set in Settings.
            </div>
            <div class="card shadow-sm table-card">
              <div v-if="capLoading" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Working out capitation…</div>
              <div v-else-if="cap" class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead class="table-head"><tr><th class="table-th">Facility</th><th class="table-th text-right">Enrollees</th><th class="table-th">Programmes</th><th class="table-th text-right">Amount</th><th class="table-th">Pays to</th></tr></thead>
                  <tbody>
                    <tr v-if="!cap.facilities.length"><td colspan="5" class="empty-state">No enrollees assigned to facilities.</td></tr>
                    <tr v-for="f in cap.facilities" :key="f.provider_id" class="table-row" :class="{ 'text-muted': f.already }">
                      <td>{{ f.name }}</td>
                      <td class="text-right">{{ fmt(f.enrollees) }}</td>
                      <td class="small">{{ f.lines.map((l) => `${l.sector} ${fmt(l.enrollees)} × ₦${fmt(l.rate)}`).join(" · ") || "—" }}</td>
                      <td class="text-right font-weight-bold">₦{{ fmt(f.amount) }}</td>
                      <td class="small">
                        <span v-if="f.already" class="badge badge-soft-info">In run {{ f.already.run_id }} ({{ f.already.status }})</span>
                        <span v-else-if="f.payout">{{ f.payout.kind === "wallet" ? "Wallet" : f.payout.bank_name }} {{ f.payout.account }}<div class="text-muted">{{ f.payout.account_name }}</div></span>
                        <span v-else class="text-danger"><i class="fe fe-alert-circle"></i> No payout account
                          <router-link :to="{ path: '/payout-account', query: { provider_id: f.provider_id } }" class="ml-1">Add</router-link></span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ── Runs ───────────────────────────────────────── -->
          <div v-if="tab === 'runs'" class="card shadow-sm table-card">
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <thead class="table-head"><tr><th class="table-th">Run</th><th class="table-th">For</th><th class="table-th text-right">Amount</th><th class="table-th">Status</th><th class="table-th">Prepared by</th><th class="table-th">Approved by</th><th class="table-th"></th></tr></thead>
                <tbody>
                  <tr v-if="!runs.length"><td colspan="7" class="empty-state">No payment runs yet.</td></tr>
                  <tr v-for="r in runs" :key="r.id" class="table-row">
                    <td>#{{ r.id }}<div class="small text-muted">{{ r.created_at | moment("D MMM YYYY") }}</div></td>
                    <td>{{ r.kind === "capitation" ? "Capitation " + monthLabel(r.period) : "Claims" }}<div class="small text-muted">{{ r.items_count }} facilities</div></td>
                    <td class="text-right font-weight-bold">₦{{ fmt(r.total_amount) }}</td>
                    <td><span class="run-status" :class="r.status">{{ statusLabel(r.status) }}</span>
                      <div class="small text-muted">{{ countsText(r.counts) }}</div></td>
                    <td class="small">{{ r.prepared_by_name }}</td>
                    <td class="small">{{ r.approved_by_name || "—" }}</td>
                    <td class="text-right"><router-link :to="`/online-payments/run/${r.id}`" class="btn btn-sm btn-outline-primary">Open</router-link></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── Settings ───────────────────────────────────── -->
          <div v-if="tab === 'settings' && settings">
            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header"><div class="section-number"><i class="fe fe-briefcase" style="font-size: 12px;"></i></div><h6 class="mb-0 font-weight-bold text-white">Agency's paying account (Safehaven)</h6></div>
              <div class="card-body">
                <p class="mb-2">Current: <b>{{ settings.debit_account_name || "not set" }}</b> {{ settings.debit_account_number || "" }}</p>
                <div v-if="settings.is_admin" class="d-flex flex-wrap align-items-end" style="gap: 8px;">
                  <div><label class="small font-weight-bold text-secondary d-block">Safehaven account number</label>
                    <input class="form-control form-control-sm" v-model.trim="debitAccount" maxlength="10" inputmode="numeric" @input="debitAccount = debitAccount.replace(/\D/g, '')" /></div>
                  <button class="btn btn-sm btn-submit" :disabled="busy || debitAccount.length !== 10" @click="saveDebit">Confirm with the bank and save</button>
                </div>
                <small v-else class="text-muted">Only agency admins can change it.</small>
              </div>
            </div>
            <div class="card shadow-sm table-card">
              <div class="form-card-header"><div class="section-number"><i class="fe fe-percent" style="font-size: 12px;"></i></div><h6 class="mb-0 font-weight-bold text-white">Capitation rates (per enrollee per month)</h6></div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm mb-3">
                    <thead class="table-head"><tr><th class="table-th">Programme</th><th class="table-th text-right">Principals</th><th class="table-th text-right">Current rate</th><th class="table-th" style="width: 180px;">New rate (₦)</th></tr></thead>
                    <tbody>
                      <tr v-for="(n, sector) in rates.programmes" :key="sector" class="table-row">
                        <td>{{ sector }}</td><td class="text-right">{{ fmt(n) }}</td>
                        <td class="text-right">{{ rates.current[sector] !== undefined ? "₦" + fmt(rates.current[sector]) : "—" }}</td>
                        <td><input type="number" min="0" step="0.01" class="form-control form-control-sm" v-model="newRates[sector]" :disabled="!settings.can_prepare" /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="settings.can_prepare" class="d-flex flex-wrap align-items-end" style="gap: 8px;">
                  <div><label class="small font-weight-bold text-secondary d-block">From month</label><input type="month" class="form-control form-control-sm" v-model="ratesFrom" /></div>
                  <button class="btn btn-sm btn-submit" :disabled="busy || !changedRates.length" @click="saveRates">Save {{ changedRates.length }} rate(s)</button>
                </div>
                <details class="mt-3" v-if="rates.history.length"><summary class="small text-muted">Rate history</summary>
                  <table class="table table-sm small mt-2"><tr v-for="(h, i) in rates.history" :key="i"><td>{{ h.effective_from }}</td><td>{{ h.sector }}</td><td class="text-right">₦{{ fmt(h.amount) }}</td></tr></table>
                </details>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import moment from "moment";
import { RUN_STATUS } from "./runStatus";


/** Online payments (payments/…): capitation preview and one-click preparation, payment runs, paying account and rates. */
export default {
  components: { Navbar },
  data() {
    return {
      tab: this.$route.query.tab || "capitation",
      tabs: [{ key: "capitation", label: "Capitation", icon: "fe-users" }, { key: "runs", label: "Payment runs", icon: "fe-list" }, { key: "settings", label: "Settings", icon: "fe-settings" }],
      settings: null, period: moment().subtract(1, "month").format("YYYY-MM"), thisMonth: moment().format("YYYY-MM"),
      cap: null, capLoading: false, runs: [], busy: false,
      debitAccount: "", rates: { current: {}, history: [], programmes: {} }, newRates: {}, ratesFrom: moment().format("YYYY-MM"),
    };
  },
  computed: {
    periodLabel() { return moment(this.period, "YYYY-MM").format("MMMM YYYY"); },
    ready() { return this.cap ? this.cap.facilities.filter((f) => f.payout && !f.already && f.amount > 0) : []; },
    readyCount() { return this.ready.length; },
    readyTotal() { return this.ready.reduce((n, f) => n + f.amount, 0); },
    changedRates() {
      return Object.entries(this.newRates).filter(([s, v]) => v !== "" && v !== null && Number(v) !== this.rates.current[s]).map(([sector, amount]) => ({ sector, amount: Number(amount) }));
    },
  },
  created() {
    this.axios.get("/api/v1/auth/payments/settings").then((r) => { this.settings = r.data.data; });
    this.load();
  },
  methods: {
    fmt(v) { return Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); },
    monthLabel(p) { return p ? moment(p, "YYYY-MM").format("MMM YYYY") : ""; },
    statusLabel(s) { return RUN_STATUS[s] || s; },
    countsText(c) {
      if (!c) return "";
      const l = { succeeded: "paid", sent: "in progress", pending: "to send", failed: "failed", no_account: "no account" };
      return Object.entries(c).map(([k, n]) => `${n} ${l[k] || k}`).join(" · ");
    },
    msg(e, f) { const d = e.response && e.response.data; return (d && (d.errors ? Object.values(d.errors).flat()[0] : d.message)) || f; },
    setTab(t) { this.tab = t; this.$router.replace({ query: { tab: t } }).catch(() => {}); this.load(); },
    load() {
      if (this.tab === "capitation") this.preview();
      if (this.tab === "runs") this.axios.get("/api/v1/auth/payments/runs").then((r) => { this.runs = r.data.data; });
      if (this.tab === "settings") this.axios.get("/api/v1/auth/payments/capitation-rates").then((r) => { this.rates = r.data.data; this.newRates = { ...r.data.data.current }; });
    },
    preview() {
      this.capLoading = true;
      this.axios.get("/api/v1/auth/payments/capitation-preview", { params: { period: this.period } })
        .then((r) => { this.cap = r.data.data; })
        .catch((e) => this.$toasted.error(this.msg(e, "Could not work out capitation."), { position: "top-center", duration: 5000 }))
        .finally(() => { this.capLoading = false; });
    },
    prepareCapitation() {
      if (!confirm(`Prepare capitation for ${this.periodLabel}: ₦${this.fmt(this.readyTotal)} to ${this.readyCount} facilities? An approver must approve it before money is sent.`)) return;
      this.busy = "cap";
      this.axios.post("/api/v1/auth/payments/runs/capitation", { period: this.period })
        .then((r) => { this.$toasted.success(r.data.message, { position: "top-center", duration: 5000 }); this.$router.push(`/online-payments/run/${r.data.data.id}`); })
        .catch((e) => this.$toasted.error(this.msg(e, "Could not prepare capitation."), { position: "top-center", duration: 6000 }))
        .finally(() => { this.busy = false; });
    },
    saveDebit() {
      this.busy = true;
      this.axios.put("/api/v1/auth/payments/settings", { debit_account_number: this.debitAccount })
        .then((r) => { this.$toasted.success(r.data.message, { position: "top-center", duration: 5000 }); this.debitAccount = ""; return this.axios.get("/api/v1/auth/payments/settings"); })
        .then((r) => { if (r) this.settings = r.data.data; })
        .catch((e) => this.$toasted.error(this.msg(e, "Could not save the account."), { position: "top-center", duration: 6000 }))
        .finally(() => { this.busy = false; });
    },
    saveRates() {
      this.busy = true;
      this.axios.put("/api/v1/auth/payments/capitation-rates", { effective_from: this.ratesFrom + "-01", rates: this.changedRates })
        .then((r) => { this.$toasted.success(r.data.message, { position: "top-center", duration: 5000 }); this.load(); })
        .catch((e) => this.$toasted.error(this.msg(e, "Could not save the rates."), { position: "top-center", duration: 6000 }))
        .finally(() => { this.busy = false; });
    },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
.page-tabs { background: #fff; border-radius: 8px; padding: 6px 8px 0; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08); }
.page-tabs .nav-link { color: #475569; font-weight: 600; }
.page-tabs .nav-link.active { color: #1f6feb; }
.btn-approve { background: linear-gradient(135deg, #047857 0%, #10b981 100%); border: none; color: #fff; border-radius: 8px; font-weight: 600; padding: 9px 16px; }
.btn-approve:disabled { opacity: 0.5; }
.run-status { font-size: 0.72rem; font-weight: 600; padding: 3px 10px; border-radius: 999px; background: #e2e8f0; color: #334155; white-space: nowrap; }
.run-status.pending_approval { background: #fef3c7; color: #92400e; }
.run-status.processing, .run-status.approved { background: #dbeafe; color: #1d4ed8; }
.run-status.completed { background: #d1fae5; color: #065f46; }
.run-status.completed_with_errors, .run-status.rejected { background: #fee2e2; color: #991b1b; }
</style>
